import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import { DEFAULT_BUSINESS, WEAR_PART_LABELS, formatPence, shelfRulesOf, type ShopsReply } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { effectsText } from './shelf-panel';
import { actionKey } from '../lib/format';

const nameOf = (person: { username: string | null; firstName: string }) =>
  person.username ? `@${person.username}` : person.firstName;

// Players' shops (as Lagos Life's): the shops open to players and what is on their shelves; buy a few at a time,
// paid to the shop's till, and it does its good at once.
export function ShopsSheet({
  open,
  focus = null,
  onClose,
  onWallet,
  onLook,
}: {
  open: boolean;
  // a shop to show first (opened from its card in a chat)
  focus?: string | null;
  onClose: () => void;
  onWallet: () => void;
  // a look was bought (a haircut, an outfit): the character changes
  onLook?: () => void;
}) {
  const content = useCatalogue()?.business ?? DEFAULT_BUSINESS;
  const rules = shelfRulesOf(content);
  const [reply, setReply] = useState<ShopsReply | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setReply(await api.business.shops());
      setFailure(null);
    } catch (error) {
      setFailure(describeError(error).message);
    }
  }, []);
  useEffect(() => {
    if (open) void load();
    else setReply(null);
  }, [open, load]);
  const buy = async (businessId: string, productId: string, count: number) => {
    setBusy(`${businessId}:${productId}`);
    try {
      const next = await api.business.buy(businessId, productId, count, actionKey('shop'));
      setReply(next);
      notify(next.message);
      onWallet();
      if (reply?.shops.some((shop) => shop.items.some((item) => item.productId === productId && item.wear))) onLook?.();
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setBusy(null);
    }
  };
  const kindOf = (id: string) => content.kinds.find((item) => item.id === id);
  const areaOf = (id: string) => content.areas.find((item) => item.id === id)?.name ?? id;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone billboard-sheet shops-sheet">
        <DialogTitle>🛍️ Players&rsquo; shops</DialogTitle>
        <DialogDescription>
          Food, coffee and more from other players&rsquo; shops. It does its good at once.
        </DialogDescription>
        {reply?.waitUntil && (
          <p className="neighbours-note">
            New in London: you can buy from players&rsquo; shops from{' '}
            {new Date(reply.waitUntil).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}.
          </p>
        )}
        {failure ? (
          <p className="neighbours-note">{failure}</p>
        ) : !reply ? (
          <p className="neighbours-note">Finding shops…</p>
        ) : focus && !reply.shops.some((shop) => shop.businessId === focus) ? (
          <p className="neighbours-note">That shop isn&rsquo;t open to players right now.</p>
        ) : !reply.shops.length ? (
          <p className="neighbours-note">No shops are open to players right now. Start one in Business!</p>
        ) : (
          <ul className="shops-list">
            {[...reply.shops]
              .sort((a, b) => Number(b.businessId === focus) - Number(a.businessId === focus))
              .map((shop) => (
                <li
                  key={shop.businessId}
                  aria-label={shop.name}
                  className={shop.businessId === focus ? 'focus' : undefined}
                >
                  <div className="shops-head">
                    <span aria-hidden="true">{kindOf(shop.kind)?.icon ?? '🛍️'}</span>
                    <div>
                      <strong>{shop.name}</strong>
                      <small>
                        {nameOf(shop.owner)} · {kindOf(shop.kind)?.label ?? shop.kind} · {areaOf(shop.area)}
                      </small>
                    </div>
                  </div>
                  <ul>
                    {shop.items.map((item) => {
                      const key = `${shop.businessId}:${item.productId}`;
                      // a look is worn one at a time
                      const most = item.wear ? 1 : Math.min(item.stock, rules.maxQty);
                      const count = Math.min(qty[key] ?? 1, most);
                      const short = (reply.walletPence ?? 0) < item.pricePence * count;
                      return (
                        <li key={item.productId}>
                          <span aria-hidden="true">{item.icon}</span>
                          <div>
                            <strong>{item.label}</strong>
                            <small>
                              {item.wear ? `${WEAR_PART_LABELS[item.wear.part]}: you wear it · ` : ''}
                              {effectsText(item.effects)} · {item.stock} left
                            </small>
                          </div>
                          <div className="shops-buy">
                            <div className="business-stepper">
                              <button
                                type="button"
                                aria-label={`One fewer ${item.label}`}
                                disabled={count <= 1}
                                onClick={() => setQty({ ...qty, [key]: count - 1 })}
                              >
                                −
                              </button>
                              <output aria-label={`How many ${item.label}`}>{count}</output>
                              <button
                                type="button"
                                aria-label={`One more ${item.label}`}
                                disabled={count >= most}
                                onClick={() => setQty({ ...qty, [key]: count + 1 })}
                              >
                                +
                              </button>
                            </div>
                            <button
                              type="button"
                              className="shops-pay"
                              disabled={shop.mine || Boolean(reply.waitUntil) || busy !== null || short}
                              onClick={() => void buy(shop.businessId, item.productId, count)}
                            >
                              {shop.mine
                                ? 'Your shop'
                                : short
                                  ? `Need ${formatPence(item.pricePence * count)}`
                                  : `Buy · ${formatPence(item.pricePence * count)}`}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
